import { useState, useCallback } from "react";
import moment from "moment";
import "moment/locale/es";
import Modal from "react-modal";
import Nav from "./Nav";
import ModalCreateEvent from "./ModalCreateEvent";
import VistaDia from "./VistaDia";
import VistaSemana from "./VistaSemana";
import VistaMes from "./VistaMes";
import VistaAño from "./VistaAño";
import Spinner from "./Spinner";
import { useAuth } from "../context/AuthContext";
import SidebarCalendario from "./SidebarCalendario";
import Breadcrumb from "./Breadcrumb";
import useCalendarData from "../hooks/useCalendarData";
import { useTranslation } from "react-i18next";

Modal.setAppElement("#root");

const CalendarioGd = () => {
  const { t } = useTranslation();
  const { user, teamOwnerId } = useAuth();
  const ownerUid = teamOwnerId || user?.uid;
  const [currentDate, setCurrentDate] = useState(moment());
  const {
    loading,
    sharedUsers,
    filters,
    proximasAVencer,
    proximosEventos,
    combinedEvents,
    fixedFilterOptions,
    filteredEvents,
    selectedFilters,
    refreshData,
    fetchCategorias,
    handleAddCategory,
    handleSaveEvent,
    handleDeleteEvent,
    toggleFixedFilter,
  } = useCalendarData(ownerUid, currentDate.year());
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState("Month view");
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingEvent, setEditingEvent] = useState(null);

  const openModal = useCallback(
    (eventData = null) => {
      // si viene de un click, eventData tendrá .nativeEvent
      if (eventData?.nativeEvent) eventData = null;

      fetchCategorias();
      setEditingEvent(eventData); // null ⇒ modo “Agregar”
      setModalIsOpen(true);
    },
    [fetchCategorias]
  );

  const closeModal = useCallback(() => {
    setModalIsOpen(false);
    setEditingEvent(null);
  }, []);

  const handlePrev = useCallback(() => {
    switch (viewMode) {
      case "Day view":
        setCurrentDate((prev) => prev.clone().subtract(1, "day"));
        break;
      case "Week view":
        setCurrentDate((prev) => prev.clone().subtract(1, "week"));
        break;
      case "Month view":
        setCurrentDate((prev) => prev.clone().subtract(1, "month"));
        break;
      case "Year view":
        setCurrentDate((prev) => prev.clone().subtract(1, "year"));
        break;
      default:
        break;
    }
  }, [viewMode]);

  const handleNext = useCallback(() => {
    switch (viewMode) {
      case "Day view":
        setCurrentDate((prev) => prev.clone().add(1, "day"));
        break;
      case "Week view":
        setCurrentDate((prev) => prev.clone().add(1, "week"));
        break;
      case "Month view":
        setCurrentDate((prev) => prev.clone().add(1, "month"));
        break;
      case "Year view":
        setCurrentDate((prev) => prev.clone().add(1, "year"));
        break;
      default:
        break;
    }
  }, [viewMode]);

  const handleToday = useCallback(() => {
    setCurrentDate(moment());
  }, []);

  // Inserta esto en tu componente, por ejemplo justo después de renderFixedFilters()
  const renderFixedFiltersWithoutTodos = (handleContextMenu) => {
    const fixedColors = {
      Festivos: "#8B5CF6",
      Eventos: "#6366F1",
      Cotizaciones: "#3B82F6",
      Proyecto: "#F472B6",
      Tareas: "#10B981",
      Trabajo: "#14B8A6",
      Personal: "#F59E0B",
      Otros: "#9CA3AF",
    };

    return fixedFilterOptions
      .filter((label) => label !== "Todos")
      .map((ff) => {
        const catObj = filters.find((f) => f.label.trim() === ff);
        const fillColor = catObj ? catObj.color : fixedColors[ff] || "#D1D5DB";

        return (
          <div
            key={ff}
            className="mb-3 flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50"
            onClick={() => toggleFixedFilter(ff)}
            onContextMenu={handleContextMenu({
              label: ff.trim().toLowerCase(),
              color: fillColor,
              ...catObj,
            })}
          >
            <span
              className="inline-flex items-center justify-center h-5 w-5 rounded-sm"
              style={{ backgroundColor: fillColor }}
            >
              {selectedFilters.includes(ff) && (
                <i className="bi bi-check text-white text-xs"></i>
              )}
            </span>
            <span
              className={`text-sm font-medium ${
                selectedFilters.includes(ff) ? "text-blue-600" : "text-gray-700"
              }`}
            >
              {ff}
            </span>
          </div>
        );
      });
  };

  const renderView = () => {
    switch (viewMode) {
      case "Day view":
        return (
          <VistaDia
            events={filteredEvents}
            currentDate={currentDate}
            onChangeDate={setCurrentDate}
            onAddEvent={openModal}
            onSave={handleSaveEvent}
            onEditEvent={openModal}
            onDeleteEvent={handleDeleteEvent}
          />
        );
      case "Week view":
        return (
          <VistaSemana
            events={filteredEvents}
            currentDate={currentDate}
            onAddEvent={openModal}
            onSave={handleSaveEvent}
            onDeleteEvent={handleDeleteEvent}
          />
        );
      case "Month view":
        return (
          <VistaMes
            events={filteredEvents}
            currentDate={currentDate}
            handleToday={handleToday}
            onSave={handleSaveEvent}
            filters={filters}
            refreshData={refreshData}
          />
        );
      case "Year view":
        return (
          <VistaAño
            events={filteredEvents}
            currentDate={currentDate}
            onSave={handleSaveEvent}
            filters={filters}
            refreshData={refreshData}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    // Mientras se cargan los datos, se muestra el Spinner (puedes personalizar el estilo)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Nav
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
        events={combinedEvents}
      />

      <div className="tailwind-scope cal-wrapper">
        <div style={{ marginTop: 10, padding: "0 2px" }}>
            <Breadcrumb
    aliases={{
      calendario: t("breadcrumbs.calendar", { defaultValue: "Calendario" }),
    }}
  />
        </div>

        <div className="flex gap-6 mt-4">
          <SidebarCalendario
            filters={filters}
            selectedFilters={selectedFilters}
            toggleFixedFilter={toggleFixedFilter}
            ownerUid={ownerUid}
            onCategoryDeleted={async () => {
              await fetchCategorias();
              await refreshData();
            }}
            renderFixedFiltersWithoutTodos={renderFixedFiltersWithoutTodos}
            isAddingCategory={isAddingCategory}
            setIsAddingCategory={setIsAddingCategory}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            handleAddCategory={handleAddCategory}
            sharedUsers={sharedUsers}
          />

          {/* Contenido del calendario */}
          <div className="flex-1 p-0">
            <div className="relative flex items-center mb-3">
              {/* Botones de vista */}
              <div className="mr-6 flex items-center justify-center border border-[#E6EDFF] rounded-lg divide-x divide-[#E6EDFF]">
                {[
                  { label: "Año", value: "Year view" },
                  { label: "Mes", value: "Month view" },
                  { label: "Semana", value: "Week view" },
                  { label: "Día", value: "Day view" },
                ].map((view, index) => (
                  <button
                    key={view.value}
                    onClick={() => setViewMode(view.value)}
                    className={`px-6 py-2 text-sm font-medium w-full focus:outline-none transition-colors duration-200 ${
                      viewMode === view.value
                        ? "text-blue-600 bg-blue-100"
                        : "text-gray-700 hover:bg-gray-100"
                    } ${index === 0 ? "rounded-l-md" : ""} ${
                      index === 3 ? "rounded-r-md" : ""
                    }`}
                    style={{ minWidth: "100px", textAlign: "center" }}
                  >
                    {view.label}
                  </button>
                ))}
              </div>

              {/* Mes y año centrado */}
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <h2 className="flex items-baseline space-x-2">
                  <span className="text-2xl font-bold text-gray-800 capitalize">
                    {currentDate.format("MMMM")}
                  </span>
                  <span className="text-lg font-medium text-gray-500">
                    {currentDate.format("YYYY")}
                  </span>
                </h2>
              </div>

              {/* Navegación + Nuevo */}
              <div className="ml-auto flex items-center gap-3">
                <div className="flex items-center rounded-md border border-[#E6EDFF] bg-white shadow-sm overflow-hidden divide-x divide-[#E6EDFF]">
                  <button
                    onClick={handlePrev}
                    className="px-3 py-1 hover:bg-gray-100 flex items-center justify-center"
                  >
                    <i className="bi bi-chevron-left text-gray-600 text-lg"></i>
                  </button>
                  <div className="flex items-center gap-3 px-3 py-1 text-gray-700 font-medium text-base">
                    <i className="bi bi-calendar-event text-gray-600 text-lg"></i>
                    <span className="capitalize">
                      {currentDate.format("MMMM")}
                    </span>
                  </div>
                  <button
                    onClick={handleNext}
                    className="px-3 py-1 hover:bg-gray-100 flex items-center justify-center"
                  >
                    <i className="bi bi-chevron-right text-gray-600 text-lg"></i>
                  </button>
                </div>

                <button
                  onClick={openModal}
                  className="bg-blue-500 text-white font-medium text-sm px-4 py-2 rounded-md hover:bg-blue-700"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  + Nuevo
                </button>
              </div>
            </div>

            {/* 👇 SOLO aquí va el borde alrededor del grid de días */}
            <div className="bg-white border border-[#E6EDFF] rounded-xl overflow-hidden mt-2">
              {renderView()}
            </div>

            <ModalCreateEvent
              isOpen={modalIsOpen}
              onClose={closeModal}
              onSave={handleSaveEvent}
              isNew={!editingEvent}
              initialData={editingEvent}
              categories={filters}
              onAddCategory={handleAddCategory}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarioGd;
